/**
 * SmartImprove Version History Drawer — Phase 6.
 *
 * Right-hand drawer listing all finalized versions for an engagement, plus
 * the current editable draft when applicable. Mirrors SmartEvaluate's
 * VersionManager UI but keyed on engagement_id and SmartImprove's snapshot
 * shape.
 *
 * Read-only by design — the only mutation here is "Create New Version"
 * (clone) which delegates to the Publish Band's flow via the parent.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { X, History, Lock, GitBranch, CheckCircle2, Eye, ImagePlus, Trash2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/smartimprove`;
const ax = axios.create({ baseURL: API, withCredentials: true });

export default function VersionHistoryDrawer({ open, onClose, engagementId, onChanged }) {
  const navigate = useNavigate();
  const { id: routeEid } = useParams();
  const [data, setData] = useState({ versions: [], draft: null, is_finalized: false });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  // Note: SnapshotPreview overlay is deprecated — `View Snapshot` now
  // navigates to `?snapshot={versionId}` and renders the full read-only
  // SnapshotPage in place. Kept here only as defensive fallback.
  const [selected, setSelected] = useState(null);

  const reload = useCallback(async () => {
    if (!engagementId) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await ax.get(`/engagements/${engagementId}/versions`);
      setData(r.data || { versions: [], draft: null, is_finalized: false });
    } catch {
      setErr("Failed to load versions");
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => {
    if (open) {
      reload();
      setSelected(null);
    }
  }, [open, reload]);

  const cloneVersion = async (versionId) => {
    try {
      const r = await ax.post(`/engagements/${engagementId}/versions/${versionId}/clone`);
      toast.success(`Draft v${r.data?.draft?.version_number} unlocked`);
      onChanged && (await onChanged());
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create new version");
    }
  };

  const viewSnapshot = (versionId) => {
    // Feb 2026: navigate to `?snapshot={versionId}` which renders the full
    // read-only SnapshotPage (replaces the legacy metadata overlay).
    const eid = engagementId || routeEid;
    if (!eid || !versionId) return;
    onClose();
    navigate(`/consultant/smartimprove/${eid}?snapshot=${versionId}`);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-40 bg-black/30"
      onClick={onClose}
      data-testid="smartimprove-version-drawer-backdrop"
    >
      <aside
        className="absolute right-0 top-0 h-full w-[440px] bg-white border-l border-[#E5E7EB] shadow-xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="smartimprove-version-drawer"
      >
        <header className="sticky top-0 bg-white border-b border-[#E5E7EB] px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-[#025979]" />
            <h2 className="text-sm font-semibold text-[#111827]">Version History</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-sm hover:bg-[#F8FAFC]"
            data-testid="version-drawer-close"
          >
            <X className="w-4 h-4 text-[#374151]" />
          </button>
        </header>

        <div className="p-5">
          <ClientLogoSection engagementId={engagementId} />

          {loading ? (
            <p className="text-xs text-[#9CA3AF]" data-testid="version-drawer-loading">
              Loading versions…
            </p>
          ) : err ? (
            <p className="text-xs text-red-600">{err}</p>
          ) : (
            <ul className="space-y-3" data-testid="version-drawer-list">
              {data.draft ? (
                <li
                  className="border border-dashed border-[#E5E7EB] rounded-sm p-3"
                  data-testid={`version-row-draft-v${data.draft.version_number}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#111827]">
                      v{data.draft.version_number}
                    </span>
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]">
                      draft (editing)
                    </span>
                  </div>
                  <p className="text-[10px] text-[#6B7280] mt-1">
                    Last edited{" "}
                    {data.draft.updated_at
                      ? new Date(data.draft.updated_at).toLocaleString()
                      : "—"}
                  </p>
                </li>
              ) : null}

              {data.versions.map((v) => {
                const isCurrent = v.id === data.current_version_id;
                return (
                  <li
                    key={v.id}
                    className="border border-[#E5E7EB] rounded-sm p-3"
                    data-testid={`version-row-v${v.version_number}`}
                  >
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="text-xs font-semibold text-[#111827] inline-flex items-center gap-1">
                        <Lock className="w-3 h-3 text-[#92400E]" /> v{v.version_number}
                      </span>
                      {isCurrent ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]">
                          <CheckCircle2 className="w-3 h-3" />
                          current
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#F8FAFC] text-[#374151] border-[#E5E7EB]">
                          finalized
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-[#6B7280] mt-1">
                      Finalized {v.created_at ? new Date(v.created_at).toLocaleString() : "—"}
                      {v.created_by?.email ? ` · by ${v.created_by.email}` : ""}
                    </p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <button
                        onClick={() => viewSnapshot(v.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold border rounded-sm border-[#E5E7EB] text-[#374151] hover:bg-[#F8FAFC]"
                        data-testid={`view-version-btn-${v.id}`}
                      >
                        <Eye className="w-3 h-3" /> View Snapshot
                      </button>
                      {isCurrent ? (
                        <button
                          onClick={() => cloneVersion(v.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold border rounded-sm border-[#025979] text-[#025979] hover:bg-[#F8FAFC]"
                          data-testid={`clone-version-btn-${v.id}`}
                        >
                          <GitBranch className="w-3 h-3" /> Create New Version
                        </button>
                      ) : null}
                    </div>
                  </li>
                );
              })}

              {!data.draft && data.versions.length === 0 ? (
                <li className="text-xs text-[#9CA3AF]">
                  No versions yet. Click <strong>Finalize Version</strong> to create v1.
                </li>
              ) : null}
            </ul>
          )}
        </div>

        {selected ? (
          <SnapshotPreview
            version={selected}
            onClose={() => setSelected(null)}
          />
        ) : null}
      </aside>
    </div>
  );
}

function SnapshotPreview({ version, onClose }) {
  const snap = version?.snapshot || {};
  const eng = snap.engagement || {};
  const gen = snap.generated || {};
  const ci = snap.client_inputs || {};
  const recs = gen.recommendations || snap.recommendations || [];
  const sRecs = gen.strategic_recommendations || snap.strategic_recommendations || [];
  const impl = snap.implementation_plans_v2 || gen.implementation || [];
  const roi = snap.roi_dashboard || (gen.roi && gen.roi[0]) || {};
  const inv = roi.investment || {};
  const sby = roi.savings_by_year || {};
  const drivers = roi.value_drivers || [];

  const money = (n) =>
    n == null ? "—" : `USD ${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const pct = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}%`);
  const months = (n) => (n == null ? "—" : `${Number(n).toFixed(1)} mo`);

  const engagementId = version?.engagement_id;
  const versionId = version?.id;
  const downloadUrl =
    engagementId && versionId
      ? `${API}/engagements/${engagementId}/versions/${versionId}/report.pdf`
      : null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-6 pb-6 px-4 overflow-y-auto"
      onClick={onClose}
      data-testid="version-snapshot-preview"
    >
      <div
        className="bg-white border border-[#E5E7EB] rounded-sm w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Read-only banner */}
        <div className="bg-[#FFF7ED] border-b border-[#FED7AA] px-5 py-2.5 flex flex-wrap items-center justify-between gap-2">
          <div className="text-[12px] text-[#9A3412]">
            <span className="font-bold">Viewing Finalized Version v{version.version_number}</span>{" "}
            (Read-only)
            <span className="text-[11px] ml-2 text-[#B45309]">
              · Finalized{" "}
              {version.created_at ? new Date(version.created_at).toLocaleString() : "—"}
              {version.created_by?.email ? ` · by ${version.created_by.email}` : ""}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-[11px] font-semibold text-[#9A3412] hover:text-[#7C2D12] px-2 py-1 rounded-sm border border-[#FED7AA] bg-white"
            data-testid="snapshot-preview-close"
          >
            Return to Draft
          </button>
        </div>

        <header className="sticky top-0 bg-white border-b border-[#E5E7EB] px-5 py-3 flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#025979]">
              SmartImprove · Executive Business Case
            </p>
            <h3
              className="text-lg font-bold text-[#0F172A]"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              {eng.company_name || "Engagement Snapshot"}
            </h3>
            <p className="text-[10px] text-[#6B7280]">
              {[eng.contact_name, eng.email, eng.industry, eng.geography].filter(Boolean).join(" · ")}
            </p>
          </div>
          {downloadUrl ? (
            <a
              href={downloadUrl}
              download
              className="inline-flex items-center gap-1 px-3 h-7 text-[11px] font-semibold bg-[#025979] hover:bg-[#024a63] text-white rounded-md"
              data-testid="snapshot-preview-download-pdf"
            >
              Download Final PDF
            </a>
          ) : null}
        </header>

        <div className="px-5 py-4 space-y-4 max-h-[78vh] overflow-y-auto">
          {/* KPI strip */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <KpiBox label="Total Investment" value={money(inv.one_time)} />
            <KpiBox label="Annual Savings" value={money(roi.annual_savings_usd)} />
            <KpiBox label="Payback" value={months(roi.overall_payback_months)} />
            <KpiBox label="3-Year ROI" value={pct(roi.overall_roi_percent)} hero />
          </section>

          {/* Value Drivers */}
          {drivers.length > 0 ? (
            <Section title="Value Drivers">
              <table className="w-full text-xs">
                <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
                  <tr>
                    <th className="px-2 py-1.5 font-semibold">Driver</th>
                    <th className="px-2 py-1.5 font-semibold text-right">Annual Savings</th>
                    <th className="px-2 py-1.5 font-semibold text-right">Recs</th>
                  </tr>
                </thead>
                <tbody>
                  {drivers.map((d) => (
                    <tr key={d.key} className="border-t border-[#F1F5F9]">
                      <td className="px-2 py-1.5">{d.label || d.key}</td>
                      <td className="px-2 py-1.5 text-right">{money(d.amount)}</td>
                      <td className="px-2 py-1.5 text-right">{d.rec_count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          ) : null}

          {/* Recommendations */}
          {recs.length > 0 || sRecs.length > 0 ? (
            <Section title={`Recommendations (${recs.length + sRecs.length})`}>
              <ul className="space-y-1">
                {[...recs, ...sRecs.map((s) => ({ ...s, is_strategic: true }))].map((r, i) => (
                  <li
                    key={(r.rec_code || r.srec_code || "REC") + "-" + i}
                    className="flex items-baseline gap-2 text-xs py-1 border-b border-dashed border-[#F1F5F9]"
                  >
                    <span className="text-[10px] font-bold text-[#025979] tabular-nums">
                      {r.rec_code || r.srec_code}
                    </span>
                    <span className="flex-1 text-[#0F172A]">{r.title || "—"}</span>
                    {r.is_strategic ? (
                      <span className="text-[9px] font-semibold text-[#075985] bg-[#F0F9FF] border border-[#BAE6FD] px-1.5 py-0.5 rounded-sm">
                        Strategic
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}

          {/* Implementation plans (finalized) */}
          {impl.length > 0 ? (
            <Section title="Implementation Plans (Finalized)">
              <table className="w-full text-xs">
                <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
                  <tr>
                    <th className="px-2 py-1.5 font-semibold">Rec</th>
                    <th className="px-2 py-1.5 font-semibold">Title</th>
                    <th className="px-2 py-1.5 font-semibold">Weeks</th>
                    <th className="px-2 py-1.5 font-semibold text-right">Cost (USD)</th>
                    <th className="px-2 py-1.5 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {impl
                    .filter((p) => (p.status || "").toLowerCase() === "finalized")
                    .map((p) => (
                      <tr key={p.id || p.rec_code} className="border-t border-[#F1F5F9]">
                        <td className="px-2 py-1.5 font-semibold text-[#025979]">{p.rec_code}</td>
                        <td className="px-2 py-1.5 text-[#0F172A]">{p.rec_title}</td>
                        <td className="px-2 py-1.5">
                          {p.estimated_weeks ? `${p.estimated_weeks}` : "—"}
                        </td>
                        <td className="px-2 py-1.5 text-right">{money(p.total_cost_usd)}</td>
                        <td className="px-2 py-1.5 text-[#047857]">Finalized</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </Section>
          ) : null}

          {/* Benefit timeline */}
          {sby && (sby.year1 != null || sby.year2 != null) ? (
            <Section title="Benefit Realization Timeline">
              <div className="grid grid-cols-3 gap-2">
                <KpiBox label="Year 1 (ramp)" value={money(sby.year1)} compact />
                <KpiBox label="Year 2 (steady)" value={money(sby.year2)} compact />
                <KpiBox label="Year 3 (steady)" value={money(sby.year3)} compact />
              </div>
              <p className="text-[10px] text-[#6B7280] mt-2">
                3-yr total <strong className="text-[#0F172A]">{money(sby.three_year_total)}</strong>{" "}
                · 3-yr net <strong className="text-[#0F172A]">{money(sby.three_year_net)}</strong>
              </p>
            </Section>
          ) : null}

          {/* Inputs reference (collapsed list) */}
          <Section title="Snapshot inputs">
            <ul className="grid grid-cols-2 md:grid-cols-4 gap-1 text-[11px] text-[#6B7280]">
              <li>
                Findings: <strong>{(ci.issues || []).length}</strong>
              </li>
              <li>
                Observations: <strong>{(ci.observations || []).length}</strong>
              </li>
              <li>
                KPIs: <strong>{(ci.kpis || []).length}</strong>
              </li>
              <li>
                Workstreams:{" "}
                <strong>{(gen.insights || []).length || (snap.workstreams || []).length}</strong>
              </li>
            </ul>
          </Section>
        </div>
      </div>
    </div>
  );
}

function KpiBox({ label, value, hero = false, compact = false }) {
  if (hero) {
    return (
      <div className="bg-[#025979] text-white rounded-sm px-2.5 py-2">
        <p className="text-[9px] uppercase tracking-wider text-white/70">{label}</p>
        <p className="text-base font-bold mt-0.5">{value}</p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm px-2.5 py-2">
      <p className="text-[9px] uppercase tracking-wider text-[#6B7280]">{label}</p>
      <p className={`${compact ? "text-sm" : "text-base"} font-bold text-[#0F172A] mt-0.5`}>
        {value}
      </p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm">
      <header className="px-3 py-1.5 border-b border-[#F1F5F9]">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#025979]">
          {title}
        </p>
      </header>
      <div className="p-3">{children}</div>
    </div>
  );
}


function ClientLogoSection({ engagementId }) {
  const [logo, setLogo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!engagementId) return;
    try {
      const r = await ax.get(`/engagements/${engagementId}/logo`);
      setLogo(r.data || null);
    } catch {
      setLogo(null);
    }
  }, [engagementId]);

  useEffect(() => {
    load();
  }, [load]);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/image\/(png|jpe?g)/i.test(file.type)) {
      setErr("Only PNG or JPG files are supported");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setErr("Logo must be under 2 MB");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      await ax.post(`/engagements/${engagementId}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Client logo uploaded");
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    setBusy(true);
    try {
      await ax.delete(`/engagements/${engagementId}/logo`);
      toast.success("Client logo removed");
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mb-4 border border-[#E5E7EB] rounded-md p-3 bg-[#FCFCFD]"
      data-testid="client-logo-section"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-semibold text-[#374151] uppercase tracking-wider">
          Client Logo
        </span>
        <span className="text-[10px] text-[#9CA3AF]">PNG / JPG · ≤ 2 MB</span>
      </div>
      <div className="flex items-center gap-3">
        <div
          className="w-16 h-10 rounded-sm border border-[#E5E7EB] bg-white flex items-center justify-center overflow-hidden"
          data-testid="client-logo-preview"
        >
          {logo?.has_logo ? (
            <img loading="lazy"
              src={logo.data_url}
              alt="Client logo"
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <span className="text-[9px] text-[#9CA3AF]">No logo</span>
          )}
        </div>
        <div className="flex-1 flex flex-wrap gap-1.5">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={onFile}
            data-testid="client-logo-input"
          />
          <Button
            size="sm"
            variant="outline"
            className="text-[11px] h-7 px-2 border-[#E5E7EB]"
            onClick={onPick}
            disabled={busy}
            data-testid="client-logo-upload-btn"
          >
            <ImagePlus className="w-3 h-3 mr-1" />
            {logo?.has_logo ? "Replace" : "Upload"}
          </Button>
          {logo?.has_logo && (
            <Button
              size="sm"
              variant="outline"
              className="text-[11px] h-7 px-2 border-[#E5E7EB] text-red-600"
              onClick={onDelete}
              disabled={busy}
              data-testid="client-logo-delete-btn"
            >
              <Trash2 className="w-3 h-3 mr-1" /> Remove
            </Button>
          )}
        </div>
      </div>
      <p className="mt-2 text-[10px] text-[#6B7280]">
        Persists across versions and will appear on PDF exports.
      </p>
      {err && (
        <p className="mt-1 text-[10px] text-red-600" data-testid="client-logo-error">
          {err}
        </p>
      )}
    </div>
  );
}

